import {ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output, signal} from '@angular/core';
import {MatButton} from "@angular/material/button";
import {IconStatusDirective} from "../../../../../../shared/directives/icon-status.directive";
import {
  advertisementStatisticFileButtons, advertisementStatisticsResourceType,
  defaultAdvertisementStatisticsFilesStatus, statisticsButtons, statisticsFileIdKeys
} from "../../configs/advertisement-statistics-modal-config";
import {RecordData} from "../../../../../types/records-table.types";
import {FilesStatus} from "../../../../../types/files-status.types";
import {
  AdvertisementStatisticsDialogData,
  AdvertisementStatisticsFormField
} from "../../types/advertisement-statistics-modal.types";
import {OpenFilesViewerModalParams} from "../../../../../types/file.types";
import {BaseModalService} from "../../../services/base-modal.service";
import {ModalsService} from "../../../../../services/modals.service";
import {
  AdvertisementInitialStatisticsModalComponent
} from "../../../advertisement-initial-statistics-modal/advertisement-initial-statistics-modal.component";
import {MatDialog} from "@angular/material/dialog";
import {
  advertisementInitialStatisticsResourceType
} from "../../../advertisement-initial-statistics-modal/configs/advertisement-initial-statistics-modal-config";
import {SetOpenMultiTypeFilesViewerModalParams} from "../../../documents-modal/types/document-files.types";
import {FilesParamsService} from "../../../files-modal/services/files-params.service";
import {AdvertisementStatisticsModalService} from "../../services/advertisement-statistics-modal.service";
import {IconStatus} from "../../../../../models/icon-status-config";
import {CurrentUserRoleAndLogin} from "../../../../../types/auth.types";

@Component({
  selector: 'div[app-advertisement-statistics-buttons]',
  imports: [
    MatButton,
    IconStatusDirective
  ],
  templateUrl: './advertisement-statistics-buttons.component.html',
  styleUrl: './advertisement-statistics-buttons.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvertisementStatisticsButtonsComponent implements OnInit {
  isEditModeActive = input<boolean>(false);
  recordData = input.required<RecordData>();
  advertisementStatisticsDialogData = input<AdvertisementStatisticsDialogData>();
  loginAndRoles = input<CurrentUserRoleAndLogin | null>();
  fetchAdvertisementInitialStatisticsData = output();

  private baseModalService: BaseModalService = inject(BaseModalService);
  private modalsService: ModalsService = inject(ModalsService);
  private dialog: MatDialog = inject(MatDialog);
  private filesParamsService: FilesParamsService = inject(FilesParamsService);
  private advertisementStatisticsModalService: AdvertisementStatisticsModalService = inject(AdvertisementStatisticsModalService);

  public readonly advertisementStatisticFileButtons = advertisementStatisticFileButtons;
  public statusData = signal<FilesStatus>(defaultAdvertisementStatisticsFilesStatus);
  public visibleInitialStatisticsBtn = computed(() => this.loginAndRoles()?.role !== 'ROLE_PROMOTION_SPECIALIST');
  private parentRecordData = computed(() => this.advertisementStatisticsDialogData()?.parentRecordData);
  private recordCalendarId = computed(() =>
    this.parentRecordData() ?
      this.baseModalService.getRecordCalendarId(this.parentRecordData()!) :
      0
  );
  private advertisementStatisticsId = computed(() => this.advertisementStatisticsDialogData()?.advertisementStatisticsId || 0);

  public async ngOnInit(): Promise<void> {
    this.advertisementStatisticsId() &&
      await this.advertisementStatisticsModalService.fetchFilesStatus(this.advertisementStatisticsId())
        .then(data => this.statusData.set(data));
  }

  public async openAdvertisementInitialStatisticsModal(): Promise<void> {
    this.dialog.open(AdvertisementInitialStatisticsModalComponent, {
      data: {
        initialAdvertisementStatisticsId: (this.parentRecordData()?.['initialStatistics'] as RecordData).id,
        parentRecordData: this.parentRecordData()
      }
    }).afterClosed().subscribe(() =>
      this.fetchAdvertisementInitialStatisticsData.emit()
    );
  }

  public async handleFileAction(field: AdvertisementStatisticsFormField): Promise<void> {
    const openFilesViewerModalParams: OpenFilesViewerModalParams = {
      title: field.label,
      key: field.key,
      recordId: this.recordCalendarId(),
      parentPath: advertisementStatisticsResourceType.path,
      recordCalendarId: this.recordCalendarId(),
      isEditMode: this.isEditModeActive()
    };

    if (!openFilesViewerModalParams.key) { return; }

    await this.modalsService.openSingleTypeFilesViewerModal(openFilesViewerModalParams);
    await this.advertisementStatisticsModalService.fetchFilesStatus(Number(this.recordData()?.id))
      .then(data => this.statusData.set(data));
  }

  public async handleOpenFiles(): Promise<void> {
    const openModalParams = this.setOpenMultiTypeFilesViewerModalParams({
      filesData: statisticsButtons,
      title: 'Файлы статистики'
    });

    await this.filesParamsService.getFilesParams(statisticsFileIdKeys, openModalParams.recordCalendarId);
    await this.modalsService.openMultiTypeFilesViewerModal(openModalParams);
    await this.advertisementStatisticsModalService.fetchFilesStatus(Number(this.recordData()?.id))
      .then(data => this.statusData.set(data));
  }

  public fetchIconStatus(statusName?: string): IconStatus {
    if (!statusName) { return 'ERROR'; }

    return statusName === 'advertisementInitialStatisticsStatus' ?
      this.baseModalService.fetchIconStatus(this.recordData()[statusName] as IconStatus) :
      this.baseModalService.fetchIconStatus(this.statusData()[statusName] as IconStatus);
  }


  private setOpenMultiTypeFilesViewerModalParams({ filesData, title }: SetOpenMultiTypeFilesViewerModalParams): OpenFilesViewerModalParams {
    return {
      title: title,
      recordCalendarId: this.recordCalendarId(),
      parentPath: advertisementInitialStatisticsResourceType.path,
      isAllFiles: true,
      recordId: Number(this.parentRecordData()?.id),
      filesData: filesData,
      keysType: statisticsFileIdKeys,
      isEditMode: this.isEditModeActive()
    };
  }
}
